import React, { useContext, useEffect, useRef, useState } from 'react'
import { FiBell, FiShoppingBag, FiMessageCircle, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { getNotificationListing, markAsReadNotification } from '../../api/context/notifications';
import Loader from '../../components/loader/loader';
import { SelectionContext } from '../../Context/Store';
import moment from 'moment';

const notificationIcon = (type) => {
    if (type === 'order') return <FiShoppingBag size={16} />;
    if (type === 'chat')  return <FiMessageCircle size={16} />;
    return <FiBell size={16} />;
};

const ViewNotifications = () => {
    const navigate = useNavigate();
    const myRef = useRef(null);

    const { notificationsAll, setNotificationsAll } = useContext(SelectionContext);
    const { setUpdateCount } = useContext(SelectionContext);

    const [loading, setLoading] = useState(false);
    const [dataCount, setDataCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 10;

    const getNotifications = (page, limit) => {
        setLoading(true);
        getNotificationListing({ page, limit })((response) => {
            if (response?.statusCode === 200) {
                setNotificationsAll(response?.data);
                setDataCount(response?.totalItems);
                setUpdateCount(response?.unreadCount);
            } else {
                console.error("Failed to fetch notifications");
            }
            setLoading(false);
        });
    };

    const handlePageChange = (nextPage) => {
        getNotifications(nextPage + 1, itemsPerPage);
        setCurrentPage(nextPage);
        myRef.current?.scrollIntoView({ block: 'start' });
    };

    const onClickmarkAsRead = (notification) => {
        const id = Number(notification?.typeId);
        markAsReadNotification(id)((response) => {
            if (response?.status) {
                getNotifications(currentPage + 1, itemsPerPage);
            }
        });

        switch (notification?.type) {
            case 'order':
                navigate('/orderdetails/' + notification?.typeId, {
                    state: { notificationId: notification?.id },
                });
                break;
            case 'chat':
                navigate('/chat-support', {
                    state: { chatId: notification?.typeId },
                });
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        getNotifications(1, itemsPerPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const totalPages = Math.max(1, Math.ceil((dataCount || 0) / itemsPerPage));
    const canPrev = currentPage > 0;
    const canNext = currentPage < totalPages - 1;

    return (
        <div className="notifications-page">
            <style>{`
                /* Match homepage: soft neutral background + consistent 16px horizontal gutter */
                .notifications-page { background: #f9fafb; min-height: calc(100vh - 70px); }
                .main-content:has(.notifications-page) { background: #f9fafb; }
                .page-content:has(.notifications-page) { background: #f9fafb; }

                body:has(.notifications-page) .page-content,
                body:has(.notifications-page) #layout-wrapper.no-topbar .page-content,
                body:has(.notifications-page) #layout-wrapper .page-content {
                    padding-top: 0 !important;
                    padding-bottom: 24px !important;
                    padding-left: 16px !important;
                    padding-right: 16px !important;
                }
                body:has(.notifications-page) .container-fluid,
                body:has(.notifications-page) .container {
                    padding-left: 0 !important;
                    padding-right: 0 !important;
                }

                /* Sticky header — bleeds edge-to-edge; horizontal padding matches .modern-panel-header */
                .notifications-page .page-header-sticky {
                    padding: clamp(14px, 1.6vw, 24px) clamp(14px, 1.4vw, 20px) clamp(10px, 1.2vw, 18px) !important;
                    margin: 0 -16px 16px !important;
                    top: 0 !important;
                    background: #f9fafb !important;
                    border-bottom: 1px solid #eef2f7 !important;
                    box-shadow: none !important;
                }
                @media (max-width: 991px) {
                    .notifications-page .page-header-sticky {
                        padding: 16px 16px 12px !important;
                    }
                }
                @media (max-width: 767.98px) {
                    .notifications-page .page-header-sticky {
                        padding: 10px 16px 8px !important;
                        margin: 0 -16px 8px !important;
                        position: static !important;
                    }
                }
                @media (max-width: 480px) {
                    .notifications-page .page-header-sticky {
                        padding: 10px 16px 8px !important;
                        margin: 0 -16px 8px !important;
                    }
                }
                .notifications-page .page-header-sticky h3 {
                    font-size: 18px;
                    font-weight: 700;
                    color: #111827;
                    margin: 0;
                    letter-spacing: -0.01em;
                }
                .notifications-page .notif-header-count {
                    font-size: 10.5px;
                    font-weight: 600;
                    color: #64748b;
                    padding: 4px 10px;
                    border-radius: 999px;
                    background: #f1f5f9;
                    letter-spacing: 0.02em;
                }

                /* List container */
                .notifications-page .notif-list {
                    background: #fff;
                    border: 1px solid #eef2f7;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 1px 2px rgba(15,23,42,0.03);
                }

                /* Row */
                .notifications-page .notif-row {
                    display: flex;
                    gap: 12px;
                    padding: 12px 14px;
                    border-bottom: 1px solid #f1f5f9;
                    cursor: pointer;
                    transition: background 0.15s ease;
                    align-items: flex-start;
                }
                .notifications-page .notif-row:last-child { border-bottom: 0; }
                .notifications-page .notif-row:hover { background: #f8fafc; }
                .notifications-page .notif-row.is-unread { background: rgba(0, 134, 149, 0.04); }
                .notifications-page .notif-row.is-unread:hover { background: rgba(0, 134, 149, 0.08); }

                .notifications-page .notif-icon-wrap {
                    position: relative;
                    width: 36px; height: 36px;
                    border-radius: 10px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: #f1f5f9;
                    color: #475569;
                    flex-shrink: 0;
                }
                .notifications-page .notif-row.is-unread .notif-icon-wrap {
                    background: rgba(0, 134, 149, 0.10);
                    color: #008695;
                }
                .notifications-page .notif-dot {
                    position: absolute;
                    top: -2px; right: -2px;
                    width: 8px; height: 8px;
                    border-radius: 50%;
                    background: #ef4444;
                    border: 2px solid #fff;
                }

                .notifications-page .notif-body { flex: 1; min-width: 0; }
                .notifications-page .notif-title {
                    font-size: 12.5px;
                    font-weight: 600;
                    color: #0f172a;
                    margin: 0 0 2px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .notifications-page .notif-row.is-unread .notif-title {
                    font-weight: 700;
                }
                .notifications-page .notif-message {
                    font-size: 11.5px;
                    color: #475569;
                    margin: 0 0 4px;
                    line-height: 1.4;
                    word-break: break-word;
                }
                .notifications-page .notif-row.is-unread .notif-message { color: #0f172a; }
                .notifications-page .notif-time {
                    font-size: 10.5px;
                    color: #94a3b8;
                    font-weight: 500;
                }

                /* Pagination */
                .notifications-page .notif-pagination {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    margin-top: 16px;
                }
                .notifications-page .np-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 4px;
                    height: 34px;
                    padding: 0 14px;
                    font-size: 12px;
                    font-weight: 600;
                    color: #0f172a;
                    background: #fff;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.15s ease;
                }
                .notifications-page .np-btn:hover:not(:disabled) {
                    border-color: #008695;
                    color: #008695;
                    background: rgba(0, 134, 149, 0.06);
                }
                .notifications-page .np-btn:disabled { opacity: 0.35; cursor: not-allowed; }
                .notifications-page .np-info {
                    font-size: 11px;
                    color: #475569;
                    font-weight: 500;
                }
                .notifications-page .np-info strong { color: #008695; font-weight: 700; }

                /* Empty state — mirrors homepage live-orders-empty */
                .notifications-page .notif-empty {
                    width: 100%;
                    min-height: 340px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 32px 20px;
                    text-align: center;
                    color: #64748b;
                    background: linear-gradient(180deg, #fafbfd 0%, #f5f7fa 100%);
                    border: 1px solid #eef2f7;
                    border-radius: 12px;
                }
                .notifications-page .notif-empty-icon {
                    font-size: 40px;
                    margin-bottom: 12px;
                    opacity: 0.7;
                    filter: grayscale(0.2);
                }
                .notifications-page .notif-empty-title {
                    font-size: 15px;
                    font-weight: 700;
                    color: #0f172a;
                    letter-spacing: -0.01em;
                    margin: 0 0 4px;
                }
                .notifications-page .notif-empty-sub {
                    font-size: 12px;
                    color: #94a3b8;
                    font-weight: 500;
                    margin: 0;
                }

                @media (max-width: 767.98px) {
                    .notifications-page .notif-list { border-radius: 8px; }
                    .notifications-page .notif-row { padding: 10px 12px; gap: 10px; }
                    .notifications-page .notif-icon-wrap { width: 32px; height: 32px; border-radius: 8px; }
                    .notifications-page .notif-title { font-size: 11.5px; }
                    .notifications-page .notif-message { font-size: 10.5px; }
                    .notifications-page .notif-time { font-size: 9.5px; }
                    .notifications-page .np-btn { height: 32px; padding: 0 10px; font-size: 11px; }
                    .notifications-page .np-info { font-size: 10.5px; }
                }
            `}</style>

            <div className="page-header-sticky d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h3 className="fw-bold">Notifications</h3>
                {dataCount > 0 && (
                    <span className="notif-header-count">{dataCount} total</span>
                )}
            </div>

            <div ref={myRef}>
                {loading ? (
                    <div className="notif-list">
                        <div style={{ height: '60vh' }}><Loader /></div>
                    </div>
                ) : notificationsAll?.length > 0 ? (
                    <>
                        <div className="notif-list">
                            {notificationsAll.map((notification, index) => {
                                const isUnread = notification?.status !== 'read';
                                return (
                                    <div
                                        key={notification?.id ?? index}
                                        className={`notif-row ${isUnread ? 'is-unread' : ''}`}
                                        onClick={() => onClickmarkAsRead(notification)}
                                        role="button"
                                    >
                                        <div className="notif-icon-wrap">
                                            {notificationIcon(notification?.type)}
                                            {isUnread && <span className="notif-dot" aria-hidden="true" />}
                                        </div>
                                        <div className="notif-body">
                                            <div className="notif-title">{notification.title}</div>
                                            <p className="notif-message">{notification.message}</p>
                                            <div className="notif-time">
                                                {moment(notification?.createdAt).format('DD MMM YY, hh:mm a')}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {totalPages > 1 && (
                            <div className="notif-pagination">
                                <button
                                    type="button"
                                    className="np-btn"
                                    onClick={() => canPrev && handlePageChange(currentPage - 1)}
                                    disabled={!canPrev}
                                >
                                    <FiChevronLeft size={14} />
                                    Prev
                                </button>
                                <span className="np-info">
                                    Page <strong>{currentPage + 1}</strong> of <strong>{totalPages}</strong>
                                </span>
                                <button
                                    type="button"
                                    className="np-btn"
                                    onClick={() => canNext && handlePageChange(currentPage + 1)}
                                    disabled={!canNext}
                                >
                                    Next
                                    <FiChevronRight size={14} />
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="notif-empty">
                        <div className="notif-empty-icon" aria-hidden="true">🔔</div>
                        <p className="notif-empty-title">No notifications yet</p>
                        <p className="notif-empty-sub">You're all caught up. New notifications will appear here.</p>
                    </div>
                )}
            </div>
        </div>
    )
}

export default ViewNotifications;
